import { infoClient } from '~/core/utils/clients/infoClient'
import { masterClient } from '~/core/utils/clients/masterClient'
import type { LabelStatus, RateType, SkipReason } from '~/features/Rates/enums'
import type { RateDeclineReasonStatusDto, RateDetailsDtoV2, RateScoreDto } from '~/features/Rates/api/chat.api.types'
import { RateStatusV2, RateListItemStatus } from '~/features/Rates/api/chat.api.enums'

const RESOURCE = '/rates'

export type RateExpectedDto = {
  appointmentId: number
  avatarPath: string
  datetimeVisitUtc: string
  doctorIdProdoctorov: number | null
  isDoctorRateRequired: boolean
  isLpuRateRequired: boolean
  isDoctorSupplementRequired: boolean
  isLpuSupplementRequired: boolean
  lpuAddress: string
  lpuAvatarPath: string
  lpuIdProdoctorov: number
  lpuName: string
  name: string
  speciality: string
  familyProfileUuid: string | null
}

export type RateSupplementPreviewDto = {
  id: number
  text: string
  dtCreated: string
  status: RateStatusV2
  hasModeratorMessage: boolean
  messagesCount: number | null
  score: RateScoreDto
  labelCode: string | null
}

export type RatePreviewDto = {
  name: string
  avatar: string
  rateType: RateType
  id: number
  rateObjectId: number,
  familyProfileUuid: string | null
  messagesCount: number | null
  dtCreated: string
  status: RateStatusV2
  info: string,
  mainText: string,
  negativeText: string,
  positiveText: string,
  hasModeratorMessage: boolean
  hasChat: boolean
  supplements: RateSupplementPreviewDto[] | null
  score: RateScoreDto
  unconfirmed: boolean
  labelCode: LabelStatus
  declineReason: RateDeclineReasonStatusDto | null
  supplementOpportunity: boolean | null
}

export type RatesDto = Record<RateListItemStatus, RatePreviewDto[]> & {rateInStatusCount: number | null}

export type RateCommentDto = {
  comment: string
  commentPos: string
  commentNeg: string
  textHelp: string
  ignoreBadWords: boolean
  id: number
}

type RateDocumentDto = {
  id: number | string
  imgS: string
  imgL?: string
  status?: string
  preview?: string
}

export type RateDocumentsDto = {
  id: number
  help: {
    text: string
    detail: string
  }
  images: RateDocumentDto[]
}

export default {
  get() {
    return masterClient.get<RatesDto>(`${RESOURCE}/v3/`, {
      camelize: true,
      camelizeProcessor:
        (key, convert) => Object.values(RateListItemStatus).includes(key as RateListItemStatus)
          ? key
          : convert(key),
    })
  },
  getRatesWithStatus(status:RateStatusV2) {
    return masterClient.get<RatesDto>(`${RESOURCE}/v3/`, {
      params: {
        status,
      },
      camelize: true,
      camelizeProcessor:
        (key, convert) => Object.values(RateStatusV2).includes(key as RateStatusV2)
          ? key
          : convert(key),
    })
  },

  deleteRate(type: string, id: number, text?: string) {
    const payload = text ? { data: { text } } : {}

    return masterClient.delete(`${RESOURCE}/v3/${type}/${id}/delete/`, payload)
  },
  getWaitingRates(payload?: { limit?: number, type?: RateType}) {
    return masterClient.get<RateExpectedDto[]>(`${RESOURCE}/v2/waiting/`, {
      params: payload,
      camelize: true,
    })
  },
  getById(type: string, id: string) {
    return masterClient.get<RateDetailsDtoV2>(`${RESOURCE}/v2/${type}/${id}/`, { camelize: true })
  },
  getComment(type: string, id: string) {
    return masterClient.get<RateCommentDto>(`${RESOURCE}/${type}/${id}/comment/`, { camelize: true })
  },
  getDocument(type: string, id: string) {
    return masterClient.get<RateDocumentsDto>(`${RESOURCE}/${type}/${id}/document/`, { camelize: true })
  },
  getSupplementDocument(type: string, id: number) {
    return masterClient.get<RateDocumentsDto>(`${RESOURCE}/v2/supplement/${type}/${id}/document/`, { camelize: true })
  },
  getPublishedRateUrl(type: string, id: string) {
    return masterClient.get<{ similarRateUrl: string }>(
      `${RESOURCE}/${type}/${id}/similar/`,
      { camelize: true },
    )
  },
  postComment({ type, id }: { type: string, id: string }, payload: Omit<RateCommentDto, 'id' | 'textHelp' | 'ignoreBadWords'>) {
    return masterClient.post(`${RESOURCE}/${type}/${id}/comment/`, payload, { camelize: true })
  },
  postDocument({ type, id }: { type: string, id: string }, payload: string) {
    return masterClient.post<RateDocumentDto>(
      `${RESOURCE}/${type}/${id}/document/`,
      { image: payload },
      { camelize: true },
    )
  },
  postSupplementDocument({ type, id }: { type: string, id: number }, payload: string) {
    return masterClient.post<{ id: number }>(
      `${RESOURCE}/v2/supplement/${type}/${id}/document/add/`,
      { image: payload },
      { camelize: true },
    )
  },
  postComplete({ type, id }: { type: string, id: string }, ignoreBadWords: boolean) {
    return masterClient.post(
      `${RESOURCE}/${type}/${id}/complete/`,
      { ignoreBadWords },
      { camelize: true },
    )
  },
  postSupplementComplete(
    query: { type: string, id: number },
    payload: { supplementText: string, ignoreBadWords: boolean},
  ) {
    return masterClient.post(
      `${RESOURCE}/v2/supplement/${query.type}/${query.id}/complete/`,
      payload,
      { camelize: true },
    )
  },
  deleteDocument({ type, id }: { type: string, id: string }, documentId: number | string) {
    return masterClient.delete<RateDocumentDto>(
      `${RESOURCE}/${type}/${id}/document/${documentId}/`,
      { camelize: true },
    )
  },
  deleteSupplementDocument({ type, id }: { type: string, id: number }, documentId: number | string) {
    return masterClient.delete(
      `${RESOURCE}/v2/supplement/${type}/${id}/document/${documentId}/`,
      { camelize: true },
    )
  },
  patchScore({ type, id }: { type: string, id: string }, payload: Record<string, number>) {
    return masterClient.patch(`${RESOURCE}/${type}/${id}/`, payload, { camelize: true })
  },
  patchSupplementScore({ type, id }: { type: string, id: number }, payload: Record<string, number>) {
    return masterClient.patch(`${RESOURCE}/v2/supplement/${type}/${id}/ratings/`, payload, { camelize: true })
  },
  skipRate(appointmentId: number, reason: SkipReason) {
    return infoClient.post(
      `/appointments/${appointmentId}/skip_rate/`,
      { rateSkipReason: reason },
      { camelize: true },
    )
  },
  publish(type: string, id: string) {
    return masterClient.post(`/rates/${type}/${id}/publish_agreement/`, {}, { camelize: true })
  },
  publishSupplement(type: string, id: number) {
    return masterClient.post(`/rates/v2/supplement/${type}/${id}/publish_agreement/`, {}, { camelize: true })
  },
  getRatesCount() {
    return masterClient.get<{ doctorRatesCount: number, lpuRatesCount: number }>(
      `${RESOURCE}/count/`,
      { camelize: true },
    )
  },
  rateInCorrectStatusExists() {
    return masterClient.get<{ correctStatusExists: boolean }>('/rates/v2/rate_in_correct_status_exists/', { camelize: true })
  },
  getNotificationReadStatus() {
    return masterClient.get<{ hasUnreadNotifications: { pdRateAccept: boolean } }>(
      '/notifications/read_status/',
      { camelize: true },
    )
  },
}
